import { getTranslatedServiceName, SupportedLocale } from "@/api/mReserve/serviceTranslations";
import { MReserveService } from "@/api/mReserve/types";
import {
  convertTimeString,
  formatDateString,
  getCountryCodeFromLocale,
  getStoreAddressString,
  PTConverter,
} from "@/apiUtils/commonUtils";
import { dateFormats } from "@/apiUtils/constants";
import { commonValidationConstants, validationConstants } from "@/apiUtils/validationConstants";
import AttentionIcon from "@/assets/icons/attention.svg?react";
import ChevronDownIcon from "@/assets/icons/chevron-down.svg?react";
import BookingToastView from "@/common/components/BookingToastView";
import IconCalendar from "@/common/core/icons/IconCalendar";
import IconClock from "@/common/core/icons/IconClock";
import IconLocation from "@/common/core/icons/IconLocation";
import {
  EyeTestMyInfoInputs,
  MyInfoSectionContent,
  StoreType,
} from "@/common/lib/types";
import Feedback from "@/components/footer/components/Feedback";
import Accordion from "@/components/ui/accordion";
import Button from "@/components/ui/button/Button";
import TextArea from "@/components/ui/textArea/TextArea";
import TextField from "@/components/ui/textField/TextField";
import { cn } from "@/lib/tailwind";
import { InputMask } from "@react-input/mask";
import { PortableText } from "next-sanity";
import Image from "next/image";
import { Dispatch, FC, ReactNode, SetStateAction, useMemo } from "react";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormHandleSubmit,
  UseFormRegister,
} from "react-hook-form";

import RenderCurrencyInText from "@/components/common/RenderCurrencyInText";

interface EyeTestUserInfoViewProps {
  locale: string;
  serviceDetails?: MReserveService | null;
  control: Control<EyeTestMyInfoInputs>;
  content?: MyInfoSectionContent;
  storeDetails?: StoreType;
  country?: string;
  selectedDate?: string;
  slotStart?: string;
  slotEnd?: string;
  errors: FieldErrors<EyeTestMyInfoInputs>;
  phoneNumberFormat: string;
  dialCode: string;
  error?: { active: boolean; message?: string };
  comments: string;
  setError: Dispatch<SetStateAction<{ active: boolean; message?: string }>>;
  register: UseFormRegister<EyeTestMyInfoInputs>;
  handleSubmit: UseFormHandleSubmit<EyeTestMyInfoInputs>;
  onSubmit: (data: EyeTestMyInfoInputs) => void;
  onSubmitIsLoading: boolean;
}

const EyeTestUserInfoView: FC<EyeTestUserInfoViewProps> = (props) => {
  const {
    content,
    locale,
    serviceDetails,
    storeDetails,
    country,
    selectedDate,
    slotStart,
    slotEnd,
    errors,
    dialCode,
    error,
    comments,
    setError,
    register,
    handleSubmit,
    onSubmit,
    control,
    onSubmitIsLoading,
  } = props;

  const { IS_MAIL } = commonValidationConstants;
  const { phoneNoValidation } = validationConstants?.[country as keyof typeof validationConstants];

  const MAX_CHARACTERS = 256;

const { title, icon } = useMemo(() => {
    // Use service icon from API if available, otherwise fallback to hardcoded icons
    const serviceIcon = serviceDetails?.service_icon;
    let icon: ReactNode;
    if (serviceIcon) {
      icon = (
        <Image
          src={serviceIcon}
          alt={serviceDetails?.name || "Service"}
          width={24}
          height={24}
          className="icon md me-4"
          style={{ objectFit: "contain" }}
          unoptimized
        />
      );
    }

    const title = getTranslatedServiceName(serviceDetails?.name ?? "", (locale as string)?.split("-")[1] as SupportedLocale);

    return { title, icon };
  }, [serviceDetails?.service_icon, serviceDetails?.name, locale]);


  const charactersLeft = useMemo(
    () => MAX_CHARACTERS - (comments?.length || 0),
    [comments?.length],
  );
  const countryCode = getCountryCodeFromLocale(locale as string);

  return (
    <>
      <BookingToastView
        type="danger"
        open={error?.active}
        message={error?.message}
        toggle={() => setError({ active: false })}
        icon={icon}
      />
      <Accordion
        expandIconRotation="rotate-180"
        headerClasses="border-none"
        expandIcon={<ChevronDownIcon height={10} width={10} />}
        items={[
          {
            classNames: {
              container: "border-none mb-5",
            },
            title: (
              <div className="flex w-full items-center justify-between">
                <RenderCurrencyInText 
                  text={content?.title} 
                  className="text-base font-bold" 
                />
              </div>
            ),
            content: storeDetails && (
              <>
                <div className="mt-md-6 mt-7 flex items-start">
                  <IconLocation className="md fill-primary me-4 h-5 w-5" />
                  <div>
                    <p className="mb-2 text-[0.875rem] font-bold">{storeDetails.name}</p>
                    <p className="text-[0.875rem] font-normal">
                      {getStoreAddressString(storeDetails.address)}
                    </p>
                  </div>
                </div>
                <div className="mt-6 flex items-center">
                  <IconCalendar className="icon md fill-primary me-4 h-5 w-5" />
                  <p className="text-[0.875rem] font-normal">
                    {selectedDate
                      ? formatDateString(new Date(selectedDate), dateFormats.DD_MMMM_YYYY)
                      : ""}
                  </p>
                </div>
                <div className="mt-4 flex items-center">
                  <IconClock className="icon md me-4" />
                  <p className="text-[0.875rem] font-normal">
                    {convertTimeString(slotStart)} - {convertTimeString(slotEnd)}
                  </p>
                </div>
                <div className="mt-4 flex items-center">
                  {icon}
                  <RenderCurrencyInText 
                    text={title} 
                    className="text-[0.875rem] font-normal" 
                  />
                </div>
              </>
            ),
          },
        ]}
      />
      <hr className="m-0" />
      <p className="mt-lg-6 mb-3 mt-5 text-[0.875rem]">{content?.pleaseFillTheForm}</p>
      <form onSubmit={handleSubmit(onSubmit)} className="mb-5">
        <div className="flex flex-col gap-5">
          <div>
            <TextField
              type="text"
              id="firstName"
              className={`form-control${errors?.firstName ? "is-invalid" : ""} w-full`}
              placeholder={content?.firstNamePlaceHolder}
              {...register("firstName", { required: content?.firstNameError })}
              slots={{
                endAction: { action: errors?.email?.message && <AttentionIcon /> },
                error: { error: errors?.firstName?.message },
                label: {
                  label: (
                    <>
                      {content?.firstName} <span>*</span>
                    </>
                  ),
                },
              }}
            />
          </div>
          <div>
            <TextField
              id="lastName"
              type="text"
              className={`form-control${errors?.lastName ? "is-invalid" : ""} w-full`}
              placeholder={content?.lastNamePlaceHolder}
              {...register("lastName", { required: content?.lastNameError })}
              slots={{
                endAction: { action: errors?.lastName?.message && <AttentionIcon /> },
                error: { error: errors?.lastName?.message },
                label: {
                  label: (
                    <>
                      {content?.lastName} <span>*</span>
                    </>
                  ),
                },
              }}
            />
          </div>
          <div>
            <TextField
              id="email"
              type="email"
              className={`form-control${errors?.email ? "is-invalid" : ""} w-full`}
              placeholder={content?.emailPlaceHolder}
              {...register("email", {
                required: content?.emailError,
                pattern: { value: IS_MAIL, message: content?.invalidEmailError ?? "" },
              })}
              slots={{
                endAction: { action: errors?.email?.message && <AttentionIcon /> },
                error: { error: errors?.email?.message },
                label: {
                  label: (
                    <>
                      {content?.email} <span>*</span>
                    </>
                  ),
                },
              }}
            />
          </div>
          <div className="flex flex-col items-start justify-center">
            <div className="flex w-full flex-col">
              <div className="flex items-center justify-between">
                <label
                  htmlFor="phone"
                  className={cn("text-[0.813rem] font-normal", errors.phone && "text-secondary-1")}
                >
                  {content?.mobileNumber} <span>*</span>
                </label>
                {/* <Tooltip text={content?.whyDoWeNeedThisMessage ?? ""}>
                  <p className="text-[0.813rem] font-normal underline peer-hover:visible">
                    {content?.whyDoWeNeedThis}
                  </p>
                </Tooltip> */}
              </div>
              <div className="flex h-[48px] w-full items-center gap-2 overflow-hidden bg-tertiary-3 px-4 py-2">
                <div className="flex flex-shrink-0 items-center gap-2">
                  <Image
                    src={`https://www.magrabi.com/magrabi/images/flags/${countryCode}.svg`}
                    width={20}
                    height={13}
                    quality={75}
                    alt={countryCode}
                  />
                  <p>{dialCode}</p>
                </div>
                <div className="h-full w-[2px] flex-shrink-0 bg-tertiary-9"></div>

                <InputMask
                  {...register("phone", {
                    required: content?.mobileNumberError,
                    pattern: {
                      value: phoneNoValidation,
                      message: content?.invalidMobileNumberError ?? "",
                    },
                    validate: {
                      noUnderscore: (value) =>
                        value.indexOf("_") === -1 || content?.invalidMobileNumberError,
                    },
                  })}
                  id="phone"
                  name="phone"
                  replacement={{ _: /\d/ }}
                  className="w-[100px] border-none bg-transparent p-0 focus:border-none focus:outline-none focus:ring-0"
                  showMask
                  mask="__-____-___"
                  placeholder="__-____-___"
                />
              </div>
              <Feedback {...errors?.phone} className="-mx-[0.4px] mt-0 text-sm text-secondary-1" />
            </div>
          </div>
          <div>
            <Controller
              name="comments"
              control={control}
              render={({ field }) => (
                <TextArea
                  {...field}
                  className="w-full focus:ring-0"
                  placeholder={content?.anyCommentsPlaceholder}
                  label={content?.anyComments}
                  maxLength={MAX_CHARACTERS}
                  rows={7}
                />
              )}
            />
            <div className="text-[0.813rem] font-normal">
              {content?.commentsLengthLabel?.replace("{{count}}", charactersLeft?.toString())}
            </div>
          </div>
          <div className="flex w-full flex-col gap-5">
            <div className="flex flex-col items-start justify-center">
              <div className="mb-0 me-3 flex items-start justify-start gap-2">
                <input
                  className="form-check-input"
                  type="radio"
                  id="terms"
                  {...register("terms", { required: content?.privacyPolicyError })}
                />
                <span className="text-[0.813rem] font-normal">
                  {content?.privacyPolicy ? <PortableText value={PTConverter(content?.privacyPolicy)} /> : ""}
                </span>
              </div>
              <Feedback
                className={cn("mt-0 block w-full pt-3", errors.terms && "text-secondary-1")}
                {...errors?.terms}
              />
            </div>
            <div className="w-full">
              <Button
                type="submit"
                isLoading={onSubmitIsLoading}
                className="w-full text-[0.875rem] font-bold"
              >
                {content?.continueButtonLabel}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default EyeTestUserInfoView;
